import { CATEGORY_CODES, getCategoryCode, SHARED_VEHICLE_ID } from './taxonomy'
import type {
  Maker,
  MappingStatus,
  Product,
  PublishStatus,
  ReviewStatus,
  SellStatus,
  SourceRecord,
  Vehicle,
} from '../schemas/catalog.schema'

export const SELL_LABEL: Record<SellStatus, string> = {
  onSale: '판매중',
  soldOut: '품절',
  stopped: '판매중지',
}

export const MAPPING_LABEL: Record<MappingStatus, string> = {
  target: '연동대상',
  hold: '보류',
  excluded: '제외',
  recheck: '재검토',
}

export const REVIEW_LABEL: Record<ReviewStatus, string> = {
  confirmed: '확인완료',
  adminCheck: '관리자 확인',
  recheck: '재검토 필요',
}

export const PUBLISH_LABEL: Record<PublishStatus, string> = {
  unpublished: '미게시',
  pending: '게시대기',
  published: '게시완료',
}

export type Lifecycle =
  'collected' | 'needsMapping' | 'confirmed' | 'pending' | 'published' | 'error'

export const LIFECYCLE_LABEL: Record<Lifecycle, string> = {
  collected: '연동완료',
  needsMapping: 'Mapping 필요',
  confirmed: '확인완료',
  pending: '게시대기',
  published: '게시완료',
  error: '오류/보완',
}

export function hasRequiredMapping(product: Product) {
  return Boolean(
    product.makerCode &&
    product.categoryCode &&
    product.categoryGroup &&
    (product.vehicleIds.length > 0 || product.makerCode === 'MIXED')
  )
}

export function getDataErrors(product: Product): string[] {
  const errors: string[] = []
  if (!product.displayName.trim()) errors.push('상품명 누락')
  if (!Number.isInteger(product.price) || product.price <= 0) {
    errors.push('가격 확인 필요')
  }
  if (!product.mainImageUrl) errors.push('대표이미지 누락')
  if (product.syncError) errors.push(`연동 오류: ${product.syncError}`)
  return errors
}

export function getLifecycle(product: Product): Lifecycle {
  if (getDataErrors(product).length > 0) return 'error'
  if (product.publishStatus === 'published') return 'published'
  if (product.publishStatus === 'pending') return 'pending'
  if (!hasRequiredMapping(product)) return 'needsMapping'
  if (product.reviewStatus === 'confirmed') return 'confirmed'
  return 'collected'
}

export function getConfirmBlockers(product: Product): string[] {
  const blockers = [...getDataErrors(product)]
  if (!product.makerCode) blockers.push('제조사 미지정')
  if (product.vehicleIds.length === 0 && product.makerCode !== 'MIXED') {
    blockers.push('차종 미지정')
  }
  if (!product.categoryCode) blockers.push('상품군 미지정')
  return blockers
}

export function getPublishBlockers(product: Product): string[] {
  const blockers = getConfirmBlockers(product)
  if (product.mappingStatus !== 'target') {
    blockers.push(`Mapping 상태가 ${MAPPING_LABEL[product.mappingStatus]}임`)
  }
  if (product.reviewStatus !== 'confirmed') {
    blockers.push(`검수 상태가 ${REVIEW_LABEL[product.reviewStatus]}임`)
  }
  if (product.sellStatus === 'stopped') blockers.push('판매중지 상품')
  if (product.changedFields.length > 0) blockers.push('원본 변경 내용 미확인')
  return blockers
}

export function getSoftWarnings(product: Product): string[] {
  const warnings: string[] = []
  if (product.origin === 'smartstore' && !product.sourceProductId) {
    warnings.push('스마트스토어 상품 ID 확인 필요')
  }
  if (product.origin === 'smartstore' && !product.sourceUrl) {
    warnings.push('원본 URL 확인 필요')
  }
  if (!product.compatNote) warnings.push('연식·세대 등 호환 안내 미입력')
  return warnings
}

export function isPublic(product: Product) {
  return (
    product.publishStatus === 'published' && product.sellStatus !== 'stopped'
  )
}

export function isPurchasable(product: Product) {
  return (
    isPublic(product) && product.sellStatus === 'onSale' && product.price > 0
  )
}

export function fitsVehicle(
  product: Pick<Product, 'vehicleIds'>,
  vehicleId: string | null
) {
  if (!vehicleId) return false
  return (
    product.vehicleIds.includes(vehicleId) ||
    product.vehicleIds.includes(SHARED_VEHICLE_ID)
  )
}

export function normalizeProductName(raw: string) {
  return raw
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeKeyword(value: string) {
  return value.toLowerCase().replace(/\s+/g, '')
}

export type MappingSuggestion = {
  makerCode: string | null
  vehicleIds: string[]
  categoryCode: string | null
  ambiguous: boolean
}

export function suggestMapping(
  name: string,
  makers: Maker[],
  vehicles: Vehicle[]
): MappingSuggestion {
  const target = normalizeKeyword(name)
  const matched = vehicles.filter((vehicle) =>
    [vehicle.model, vehicle.nameKo, ...vehicle.aliases].some((keyword) =>
      target.includes(normalizeKeyword(keyword))
    )
  )
  const vehicleIds = matched.map((vehicle) => vehicle.id)
  const makerFromVehicle =
    new Set(matched.map((vehicle) => vehicle.makerCode)).size === 1
      ? matched[0].makerCode
      : null
  const makerFromName =
    makers.find(
      (maker) =>
        !maker.isShared &&
        (target.includes(normalizeKeyword(maker.name)) ||
          target.includes(normalizeKeyword(maker.nameEn)))
    )?.code ?? null
  const categoryCode =
    CATEGORY_CODES.find((category) =>
      category.keywords.some((keyword) =>
        target.includes(normalizeKeyword(keyword))
      )
    )?.code ?? null

  return {
    makerCode: makerFromVehicle ?? makerFromName,
    vehicleIds,
    categoryCode,
    ambiguous: vehicleIds.length !== 1 || !categoryCode,
  }
}

export const SOURCE_FIELD_LABEL: Record<string, string> = {
  name: '상품명',
  price: '가격',
  images: '이미지',
  options: '옵션',
  status: '판매상태',
}

export function diffSource(product: Product, record: SourceRecord): string[] {
  const source = product.source
  if (!source) return ['name', 'price', 'images', 'options', 'status']
  const changed: string[] = []
  if (source.name !== normalizeProductName(record.name)) changed.push('name')
  if (source.price !== record.price) changed.push('price')
  if (
    source.mainImageUrl !== record.mainImageUrl ||
    source.imageList.join('|') !== record.imageList.join('|')
  ) {
    changed.push('images')
  }
  if (source.options.join('|') !== record.options.join('|'))
    changed.push('options')
  if (source.status !== record.status) changed.push('status')
  return changed
}

export function createProductFromSource(
  record: SourceRecord,
  id: string,
  rank: number,
  makers: Maker[],
  vehicles: Vehicle[],
  now: string,
  actor: string
): Product {
  const name = normalizeProductName(record.name)
  const suggestion = suggestMapping(name, makers, vehicles)
  const category = getCategoryCode(suggestion.categoryCode)
  const errors: string[] = []
  if (!Number.isInteger(record.price) || record.price <= 0)
    errors.push('가격 비정상')
  if (!record.mainImageUrl) errors.push('대표이미지 없음')
  return {
    id,
    origin: 'smartstore',
    sourceKey: record.sourceKey,
    sourceProductId: record.sourceProductId,
    sourceUrl: record.sourceUrl,
    source: {
      name,
      price: record.price,
      mainImageUrl: record.mainImageUrl,
      imageList: record.imageList,
      options: record.options,
      status: record.status,
      description: record.description,
      collectedAt: now,
    },
    sourceType: '',
    displayName: name,
    summary: '',
    price: record.price,
    mainImageUrl: record.mainImageUrl,
    imageList: record.imageList,
    options: record.options.map((optionName, index) => ({
      id: `opt-${index + 1}`,
      name: optionName,
      extraPrice: 0,
    })),
    sellStatus: record.status,
    makerCode: suggestion.makerCode,
    vehicleIds: suggestion.ambiguous ? [] : suggestion.vehicleIds,
    categoryGroup: category?.group ?? null,
    categoryCode: category?.code ?? null,
    mappingStatus: suggestion.ambiguous ? 'recheck' : 'target',
    reviewStatus: 'adminCheck',
    publishStatus: 'unpublished',
    benefits: [],
    compatNote: '',
    installSteps: [],
    specs: '',
    memo: suggestion.ambiguous
      ? '자동 Mapping 후보 확정 불가. 원본 확인 필요.'
      : '',
    isBest: false,
    isNew: true,
    rank,
    listPrice: null,
    discountRate: 0,
    detailImages: [],
    infoRows: [],
    changedFields: [],
    syncError: errors.length > 0 ? errors.join(', ') : null,
    lastSyncedAt: now,
    publishedAt: null,
    updatedAt: now,
    updatedBy: actor,
  }
}

export function calcDiscountRate(listPrice: number | null, price: number) {
  if (!listPrice || listPrice <= price) return 0
  return Math.floor(((listPrice - price) / listPrice) * 100)
}
